import React, { useState, useEffect } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";

const ReportsManagement = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("ALL");
    const [filterType, setFilterType] = useState("ALL");
    const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0 });
    const [unblockingId, setUnblockingId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchReports(pagination.page);
    }, [pagination.page]);

    const fetchReports = async (page = 0) => {
        setLoading(true);
        try {
            const response = await AdminService.getReports({ page, size: pagination.size });
            if (response.data && response.data.status) {
                const pageData = response.data.data;
                setReports(pageData.content || []);
                setPagination(prev => ({ ...prev, totalPages: pageData.totalPages }));
            } else {
                toast.error("Failed to fetch reports");
            }
        } catch (error) {
            console.error("Error fetching reports:", error);
            toast.error("An error occurred while fetching reports");
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (reportId, action) => {
        const actionLabels = {
            'BLOCKED': 'Block Product',
            'PERMANENTLY_BLOCKED': 'Permanently Block (cannot be undone)',
            'RESTORED': 'Restore to Active',
            'NONE': 'Dismiss Report'
        };
        const confirmMsg = `Are you sure you want to: ${actionLabels[action] || action}?`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const response = await AdminService.takeReportAction(reportId, action);
            if (response.data && response.data.status) {
                toast.success(`Action "${actionLabels[action] || action}" applied successfully`);
                fetchReports(pagination.page);
            } else {
                toast.error(response.data.message || "Failed to take action");
            }
        } catch (error) {
            console.error("Action error:", error);
            const errMsg = error.response?.data?.data || error.response?.data?.message || "An error occurred while taking action";
            toast.error(errMsg);
        }
    };

    const handleUnblock = async (productId, productTitle) => {
        if (!window.confirm(`Unblock "${productTitle || 'Product #' + productId}"? It will become visible in listings again and the owner will receive an email notice.`)) return;
        setUnblockingId(productId);
        try {
            const response = await AdminService.unblockProduct(productId);
            if (response.data && response.data.status) {
                toast.success("Product unblocked! It is now visible in listings and owner has been notified.");
                fetchReports(pagination.page);
            } else {
                toast.error(response.data?.message || "Failed to unblock product");
            }
        } catch (error) {
            const errMsg = error.response?.data?.data || error.response?.data?.message || "An error occurred";
            toast.error(errMsg);
        } finally {
            setUnblockingId(null);
        }
    };

    const filteredReports = reports.filter(report => {
        const statusMatch = filterStatus === "ALL" || report.status === filterStatus;
        const typeMatch = filterType === "ALL" || report.targetType === filterType;
        return statusMatch && typeMatch;
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "PENDING": return "admin-badge pending";
            case "REVIEWED": return "admin-badge active";
            case "IGNORED": return "admin-badge expired";
            default: return "admin-badge";
        }
    };

    const getModerationBadge = (moderationStatus) => {
        if (!moderationStatus || moderationStatus === "ACTIVE") {
            return { bg: '#e8f5e9', color: '#2e7d32', label: 'Active', icon: 'fa-check-circle' };
        }
        switch (moderationStatus) {
            case "DISABLED": return { bg: '#fff3e0', color: '#e65100', label: 'Auto-Disabled', icon: 'fa-shield' };
            case "BLOCKED": return { bg: '#fce4ec', color: '#c62828', label: 'Blocked', icon: 'fa-ban' };
            case "PERMANENTLY_BLOCKED": return { bg: '#f3e5f5', color: '#6a1b9a', label: 'Permanent Block', icon: 'fa-lock' };
            default: return { bg: '#f5f5f5', color: '#757575', label: moderationStatus || 'Active', icon: 'fa-info-circle' };
        }
    };

    const stats = {
        total: reports.length,
        pending: reports.filter(r => r.status === 'PENDING').length,
        reviewed: reports.filter(r => r.status === 'REVIEWED').length,
        actioned: reports.filter(r => r.actionTaken !== 'NONE').length
    };

    return (
        <div className="admin-container">
            {/* Statistics Dashboard */}
            <div className="admin-kpi-grid" style={{ marginBottom: '30px' }}>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon users" style={{ background: 'linear-gradient(135deg, #607d8b, #455a64)' }}>
                        <i className="fa fa-flag"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{stats.total}</h4>
                        <p>Total Reports</p>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon ads" style={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)' }}>
                        <i className="fa fa-clock-o"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{stats.pending}</h4>
                        <p>Pending Review</p>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon revenue" style={{ background: 'linear-gradient(135deg, #4caf50, #388e3c)' }}>
                        <i className="fa fa-check-square-o"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{stats.reviewed}</h4>
                        <p>Reviewed</p>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon support" style={{ background: 'linear-gradient(135deg, #f44336, #d32f2f)' }}>
                        <i className="fa fa-shield"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{stats.actioned}</h4>
                        <p>Action Taken</p>
                    </div>
                </div>
            </div>

            <div className="admin-card" style={{ marginBottom: '25px' }}>
                <div className="admin-card-body" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', padding: '20px' }}>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                            <i className="fa fa-filter"></i> Filter by Status
                        </label>
                        <select className="admin-filter-select" style={{ width: '100%' }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="ALL">All Status</option>
                            <option value="PENDING">Pending Review</option>
                            <option value="REVIEWED">Reviewed</option>
                            <option value="IGNORED">Ignored / Dismissed</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                            <i className="fa fa-tags"></i> Filter by Type
                        </label>
                        <select className="admin-filter-select" style={{ width: '100%' }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                            <option value="ALL">All Content Types</option>
                            <option value="LISTING">Product Listings</option>
                            <option value="USER">User Profiles</option>
                        </select>
                    </div>
                    <div style={{ flex: 1 }}>
                        <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => fetchReports(pagination.page)} style={{ height: '38px' }}>
                            <i className="fa fa-refresh"></i> Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="admin-card">
                <div className="admin-card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="text-center p-5">
                            <i className="fa fa-spinner fa-spin fa-2x" style={{ color: 'var(--admin-primary)' }}></i>
                            <p style={{ marginTop: '10px', color: '#666' }}>Loading reports data...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Report ID</th>
                                        <th>Reported Item</th>
                                        <th>Reason & Context</th>
                                        <th>Reporter</th>
                                        <th>Reports</th>
                                        <th>Reported At</th>
                                        <th>Status</th>
                                        <th>Moderation</th>
                                        <th>Result</th>
                                        <th style={{ textAlign: 'right' }}>
                                            Moderation Tools
                                            <div style={{ fontSize: '9px', fontWeight: 'normal', color: '#999', marginTop: '2px' }}>
                                                Action notifies user via email
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredReports.length > 0 ? (
                                        filteredReports.map((report) => {
                                            const modBadge = getModerationBadge(report.moderationStatus);
                                            return (
                                                <tr key={report.id}>
                                                    <td style={{ fontWeight: '600', color: '#666' }}>#{report.id}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            {report.targetImageUrl ? (
                                                                <img
                                                                    src={report.targetImageUrl}
                                                                    alt="Target"
                                                                    style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' }}
                                                                />
                                                            ) : (
                                                                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                                                                    <i className={`fa ${report.targetType === 'LISTING' ? 'fa-image' : 'fa-user'}`}></i>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <div style={{ fontWeight: '600', fontSize: '13px', color: '#333', lineHeight: '1.2' }}>
                                                                    {report.targetType === 'LISTING' ? (
                                                                        <Link to={`/admin/ads/view/${report.targetId}`} style={{ color: 'var(--admin-primary)', textDecoration: 'none' }}>
                                                                            {report.targetTitle || `Listing #${report.targetId}`}
                                                                        </Link>
                                                                    ) : (
                                                                        <span>{report.targetTitle || `User #${report.targetId}`}</span>
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                                                    By: <span style={{ color: '#555' }}>{report.targetOwnerName || 'Unknown'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: '600', color: '#333' }}>{report.reasonCode}</div>
                                                        <div className="text-muted" style={{ fontSize: '12px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={report.description}>
                                                            {report.description || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No details provided</span>}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '13px' }}>
                                                            {report.reporterName ? (
                                                                <span style={{ fontWeight: '600' }}>{report.reporterName}</span>
                                                            ) : (
                                                                <>User <span style={{ fontWeight: '600' }}>#{report.reporterId}</span></>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            background: report.totalReportsOnTarget >= 5 ? '#ffebee' : '#e3f2fd',
                                                            color: report.totalReportsOnTarget >= 5 ? '#c62828' : '#1565c0',
                                                            padding: '3px 10px', borderRadius: '12px', fontWeight: '700', fontSize: '12px'
                                                        }}>
                                                            {report.totalReportsOnTarget || 1}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '13px', color: '#666' }}>
                                                        {dayjs(report.createdAt).format("MMM DD, YYYY")}<br />
                                                        <small>{dayjs(report.createdAt).format("HH:mm")}</small>
                                                    </td>
                                                    <td>
                                                        <span className={getStatusBadgeClass(report.status)}>
                                                            {report.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            background: modBadge.bg, color: modBadge.color,
                                                            padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                                                            whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: `1px solid ${modBadge.color}22`
                                                        }}>
                                                            <i className={`fa ${modBadge.icon}`} style={{ fontSize: '10px' }}></i>
                                                            {modBadge.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {report.actionTaken !== 'NONE' ? (
                                                            <span style={{
                                                                background: '#e3f2fd', color: '#1976d2',
                                                                padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #1976d222'
                                                            }}>
                                                                {report.actionTaken}
                                                            </span>
                                                        ) : <span style={{ color: '#ccc' }}>—</span>}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap', alignItems: 'center' }}>
                                                            {/* Moderation Tool Toggle: Active Indicator, Unblock Button, or Permanent Block Sticker */}
                                                            {report.targetType === 'LISTING' && (
                                                                (!report.moderationStatus || report.moderationStatus === 'ACTIVE') ? (
                                                                    <span style={{
                                                                        fontSize: '11px', fontWeight: '800', color: '#2e7d32',
                                                                        padding: '4px 12px', borderRadius: '4px', background: '#e8f5e9',
                                                                        border: '1px solid #c8e6c9', marginRight: '5px', whiteSpace: 'nowrap'
                                                                    }}>
                                                                        <i className="fa fa-check-circle"></i>
                                                                    </span>
                                                                ) : (report.moderationStatus === 'BLOCKED' || report.moderationStatus === 'DISABLED') ? (
                                                                    <button
                                                                        className="admin-btn admin-btn-sm"
                                                                        title="Unblock this product (make visible in listings)"
                                                                        onClick={() => handleUnblock(report.targetId, report.targetTitle)}
                                                                        disabled={unblockingId === report.targetId}
                                                                        style={{ background: '#2e7d32', borderColor: '#2e7d32', color: '#fff', minWidth: '32px', padding: '4px 12px' }}
                                                                    >
                                                                        {unblockingId === report.targetId ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-unlock"></i>}
                                                                    </button>
                                                                ) : report.moderationStatus === 'PERMANENTLY_BLOCKED' ? (
                                                                    <span style={{
                                                                        fontSize: '11px', fontWeight: '800', color: '#6a1b9a',
                                                                        padding: '4px 12px', borderRadius: '4px', background: '#f3e5f5',
                                                                        border: '1px solid #e1bee7', marginRight: '5px', whiteSpace: 'nowrap'
                                                                    }}>
                                                                        <i className="fa fa-lock"></i>
                                                                    </span>
                                                                ) : null
                                                            )}

                                                            {/* 2. View User — always visible */}
                                                            <button
                                                                className="admin-btn admin-btn-outline admin-btn-sm"
                                                                title="View User's Reported Products"
                                                                onClick={() => report.targetOwnerId ? navigate(`/admin/reports/user/${report.targetOwnerId}`) : null}
                                                                style={{ borderColor: '#00796b', color: '#00796b', minWidth: '32px', opacity: report.targetOwnerId ? 1 : 0.4 }}
                                                                disabled={!report.targetOwnerId}
                                                            >
                                                                <i className="fa fa-user"></i>
                                                            </button>

                                                            {report.status === 'PENDING' ? (
                                                                <>
                                                                    {/* 3. Block */}
                                                                    <button
                                                                        className="admin-btn admin-btn-danger admin-btn-sm"
                                                                        title="Block Product"
                                                                        onClick={() => handleAction(report.id, 'BLOCKED')}
                                                                        style={{ background: '#e65100', borderColor: '#e65100', minWidth: '32px' }}
                                                                    >
                                                                        <i className="fa fa-ban"></i>
                                                                    </button>

                                                                    {/* 4. Permanent Block */}
                                                                    <button
                                                                        className="admin-btn admin-btn-danger admin-btn-sm"
                                                                        title="Permanent Block (cannot be undone)"
                                                                        onClick={() => handleAction(report.id, 'PERMANENTLY_BLOCKED')}
                                                                        style={{ background: '#6a1b9a', borderColor: '#6a1b9a', minWidth: '32px' }}
                                                                    >
                                                                        <i className="fa fa-lock"></i>
                                                                    </button>

                                                                    {/* 5. Restore OR Dismiss */}
                                                                    {report.moderationStatus && report.moderationStatus !== 'ACTIVE' && report.moderationStatus !== 'PERMANENTLY_BLOCKED' ? (
                                                                        <button
                                                                            className="admin-btn admin-btn-sm"
                                                                            title="Restore to Active"
                                                                            onClick={() => handleAction(report.id, 'RESTORED')}
                                                                            style={{ background: '#2e7d32', borderColor: '#2e7d32', color: '#fff', minWidth: '32px' }}
                                                                        >
                                                                            <i className="fa fa-undo"></i>
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="admin-btn admin-btn-outline admin-btn-sm"
                                                                            title="Dismiss Report"
                                                                            onClick={() => handleAction(report.id, 'NONE')}
                                                                            style={{ minWidth: '32px' }}
                                                                        >
                                                                            <i className="fa fa-eye-slash"></i>
                                                                        </button>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                                                                    <i className="fa fa-check-circle-o"></i> Handled on {dayjs(report.actionAt).format("MMM DD")}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="10" className="text-center p-5">
                                                <div style={{ opacity: 0.5 }}>
                                                    <i className="fa fa-folder-open-o fa-3x"></i>
                                                    <p style={{ marginTop: '10px' }}>No reports found matching your selected filters.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {!loading && pagination.totalPages > 1 && (
                <div className="admin-pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '30px' }}>
                    <button
                        className="admin-btn admin-btn-outline admin-btn-sm"
                        disabled={pagination.page === 0}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                        <i className="fa fa-chevron-left"></i> Previous
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>Page {pagination.page + 1} of {pagination.totalPages}</span>
                    <button
                        className="admin-btn admin-btn-outline admin-btn-sm"
                        disabled={pagination.page >= pagination.totalPages - 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                        Next <i className="fa fa-chevron-right"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ReportsManagement;
